/**
 * DeviceInstanceModel - Pure data layer for device instances on the canvas
 * 
 * Wraps state.devices with CRUD operations. No DOM, no side effects beyond data mutation.
 * 
 * Usage:
 *   import { createDeviceInstanceModel } from './models/DeviceInstanceModel.js';
 *   const instanceModel = createDeviceInstanceModel(state);
 *   // Now use instanceModel.getAll(), instanceModel.add(), etc.
 */

import { generateId } from '../utils/index.js';

/**
 * Create a DeviceInstanceModel that wraps the devices array in the given state object.
 * 
 * @param {Object} state - The state object containing state.devices array
 * @returns {DeviceInstanceModel}
 */
export function createDeviceInstanceModel(state) {
    return {
        // ============= READ OPERATIONS =============
        
        /**
         * Get all devices
         * @returns {Device[]}
         */
        getAll() {
            return state.devices;
        },
        
        /**
         * Get device by ID
         * @param {string} id
         * @returns {Device|undefined}
         */
        getById(id) {
            return state.devices.find(d => d.id === id);
        },
        
        /**
         * Get all devices using a specific template
         * @param {string} templateId
         * @returns {Device[]}
         */
        getByTemplateId(templateId) {
            return state.devices.filter(d => d.templateId === templateId);
        },
        
        /**
         * Get device count
         * @returns {number}
         */
        getCount() {
            return state.devices.length;
        },
        
        /**
         * Count devices using a specific template
         * @param {string} templateId
         * @returns {number}
         */
        countByTemplateId(templateId) {
            return state.devices.filter(d => d.templateId === templateId).length;
        },
        
        /**
         * Check if a device exists
         * @param {string} id
         * @returns {boolean}
         */
        exists(id) {
            return state.devices.some(d => d.id === id);
        },
        
        /**
         * Check if any device matches predicate
         * @param {function(Device): boolean} predicate
         * @returns {boolean}
         */
        some(predicate) {
            return state.devices.some(predicate);
        },
        
        /**
         * Filter devices by predicate
         * @param {function(Device): boolean} predicate
         * @returns {Device[]}
         */
        filter(predicate) {
            return state.devices.filter(predicate);
        },
        
        /**
         * Map over all devices
         * @param {function(Device): T} mapper
         * @returns {T[]}
         */
        map(mapper) {
            return state.devices.map(mapper);
        },
        
        /**
         * Iterate over all devices
         * @param {function(Device): void} callback
         */
        forEach(callback) {
            state.devices.forEach(callback);
        },
        
        /**
         * Get multiple devices by IDs (filters out non-existent)
         * @param {string[]} ids
         * @returns {Device[]}
         */
        getByIds(ids) {
            return ids.map(id => state.devices.find(d => d.id === id)).filter(Boolean);
        },
        
        // ============= WRITE OPERATIONS =============
        
        /**
         * Add a device
         * @param {Device} device
         * @returns {Device} The added device
         */
        add(device) {
            state.devices.push(device);
            return device;
        },
        
        /**
         * Add multiple devices
         * @param {Device[]} devices
         */
        addMany(devices) {
            devices.forEach(d => state.devices.push(d));
        },
        
        /**
         * Remove a device by ID
         * @param {string} id
         * @returns {Device|undefined} The removed device, if found
         */
        remove(id) {
            const index = state.devices.findIndex(d => d.id === id);
            if (index === -1) return undefined;
            const [removed] = state.devices.splice(index, 1);
            return removed;
        },
        
        /**
         * Remove all devices using a specific template
         * @param {string} templateId
         * @returns {Device[]} The removed devices
         */
        removeByTemplateId(templateId) {
            const toRemove = state.devices.filter(d => d.templateId === templateId);
            state.devices = state.devices.filter(d => d.templateId !== templateId);
            return toRemove;
        },
        
        /**
         * Remove multiple devices by IDs
         * @param {string[]} ids
         * @returns {Device[]} The removed devices
         */
        removeMany(ids) {
            const idSet = new Set(ids);
            const removed = state.devices.filter(d => idSet.has(d.id));
            state.devices = state.devices.filter(d => !idSet.has(d.id));
            return removed;
        },
        
        /**
         * Clear all devices
         * @returns {Device[]} The removed devices
         */
        clear() {
            const removed = [...state.devices];
            state.devices = [];
            return removed;
        },
        
        /**
         * Update a device by ID (merges changes)
         * @param {string} id
         * @param {Partial<Device>} changes
         * @returns {Device|undefined} The updated device, if found
         */
        update(id, changes) {
            const device = state.devices.find(d => d.id === id);
            if (!device) return undefined;
            Object.assign(device, changes);
            return device;
        },
        
        /**
         * Update device position
         * @param {string} id
         * @param {{x: number, y: number}} position
         * @returns {Device|undefined}
         */
        updatePosition(id, position) {
            const device = state.devices.find(d => d.id === id);
            if (!device) return undefined;
            device.position = { ...position };
            return device;
        },
        
        /**
         * Replace all devices (for loading patches)
         * @param {Device[]} devices
         */
        replaceAll(devices) {
            state.devices = devices;
        },
        
        // ============= FACTORY =============
        
        /**
         * Create and add a new device
         * @param {string} templateId
         * @param {{x: number, y: number}} position
         * @returns {Device}
         */
        create(templateId, position = { x: 100, y: 100 }) {
            const device = {
                id: generateId(),
                templateId,
                position: { ...position }
            };
            state.devices.push(device);
            return device;
        },
        
        /**
         * Clone a device with new ID
         * @param {Device} device
         * @param {{x: number, y: number}} [position] - Optional new position
         * @returns {Device}
         */
        clone(device, position) {
            const cloned = {
                ...JSON.parse(JSON.stringify(device)),
                id: generateId(),
                position: position ? { ...position } : { ...device.position }
            };
            state.devices.push(cloned);
            return cloned;
        }
    };
}
